import type { TFunction } from 'i18next';

/** Field name to a message for the person, ready to show under that field. */
export type FieldErrors = Partial<Record<string, string>>;

interface RawFieldError {
  field: string;
  code: string;
  message: string;
}

/** The `errors` of a 400 validation response, or null for any other failure. */
export function validationErrors(e: unknown): RawFieldError[] | null {
  const response = (e as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 400) return null;
  const data = response.data as { error?: unknown; errors?: unknown; message?: unknown } | null;
  if (data?.error !== 'validation_error') return null;
  if (!Array.isArray(data.errors) || data.errors.length === 0) {
    return [
      {
        field: '',
        code: 'invalid',
        message: typeof data.message === 'string' ? data.message : '',
      },
    ];
  }
  return data.errors.filter(
    (x): x is RawFieldError => !!x && typeof x === 'object' && typeof x.field === 'string'
  );
}

/** Known field and code pairs read from `form.fieldErrors`; anything else keeps the server's text. */
export function fieldErrorMessages(errors: RawFieldError[], t: TFunction): FieldErrors {
  const out: FieldErrors = {};
  for (const e of errors) {
    if (out[e.field]) continue;
    out[e.field] = t(`form.fieldErrors.${e.field || 'form'}.${e.code}`, {
      defaultValue: e.message || t('form.fieldErrors.fallback'),
    });
  }
  return out;
}
